import type { AdminSensorOverviewItem } from '../../../functions/src/api/adminSensors';
import { SensorType } from '../../general/enums';
import type { AnomalyEvent } from '../Monitoring/anomalies/types';
import type { MonitoringDeviceRow, MonitoringOverview } from '../Monitoring/types';
import type { UnifiedDevice } from '../Monitoring/Heartbeat/useDeviceHeartbeats';
import { matchesOperatingMode, type ModeFilter } from '../Monitoring/Heartbeat/presentation';

export type { AdminSensorOverviewItem };

export interface FleetDevice extends AdminSensorOverviewItem {
  key: string;
  detailPath: string | null;
  // No row means unknown coverage/data, never an inferred healthy state.
  monitoring: MonitoringDeviceRow | null;
  // These are loaded events only; the anomaly API is paginated.
  anomalies: AnomalyEvent[];
  heartbeat: UnifiedDevice | null;
}

export interface FleetFilters {
  search: string;
  type: string;
  orgId: string;
  status: 'all' | AdminSensorOverviewItem['status'];
  attention: 'all' | 'alarms' | 'anomalies' | 'unmonitored';
  mode?: ModeFilter;
  unified?: boolean;
}

const typeLabels: Record<string, string> = {
  [SensorType.MACHINE_ACTIVITY]: 'Maskinaktivitet',
  [SensorType.FOOBOT]: 'Foobot',
  [SensorType.ESP_AQ]: 'Luftkvalitet (ESP)',
  [SensorType.ESP_AQ_NEW]: 'Luftkvalitet (ESP, nyere type)',
  [SensorType.GSM_PLANT]: 'GSM-anlæg',
  [SensorType.GSM_CUSTOM]: 'SMS-alarm',
  [SensorType.GPS_SIMTRACKING]: 'GPS SimTracking',
  [SensorType.GPS_GY_NEO]: 'GPS GY NEO',
  GPS_GY_NEO: 'GPS GY NEO',
  [SensorType.LEVEL_SENSOR_INTERNAL]: 'Niveausensor',
  [SensorType.PLANT_MODULE]: 'Anlægsmodul',
  [SensorType.DUST_OLD]: 'Støvsensor (ældre)',
  unknown: 'Ukendt type',
};

export function sensorTypeLabel(type: string): string {
  return Object.prototype.hasOwnProperty.call(typeLabels, type)
    ? typeLabels[type]
    : type || typeLabels.unknown;
}

export function sensorDetailPath(orgId: string, sensorId: string, type: string): string | null {
  if (!orgId || !sensorId) return null;
  const base = `/orgs/${encodeURIComponent(orgId)}/sensors/${encodeURIComponent(sensorId)}`;
  switch (type) {
    case SensorType.MACHINE_ACTIVITY: return `${base}/machine-activity`;
    case SensorType.FOOBOT:
      return `${base}/aq`;
    case SensorType.ESP_AQ:
    case SensorType.ESP_AQ_NEW:
      return `${base}/air-quality`;
    case SensorType.GSM_PLANT:
      return `${base}/gsm-plant`;
    case SensorType.GSM_CUSTOM:
      return `${base}/gsm-custom`;
    case SensorType.GPS_SIMTRACKING:
      return `${base}/gps-simtracking`;
    case SensorType.GPS_GY_NEO:
    case 'GPS_GY_NEO':
      return base;
    case SensorType.LEVEL_SENSOR_INTERNAL:
      return `${base}/level-sensor`;
    case SensorType.PLANT_MODULE:
      return `${base}/plant-module`;
    default:
      // The generic detail route is the older GPS page, not a fallback for every family.
      return null;
  }
}

export function supportsAnomalies(type: string): boolean {
  return (
    type === SensorType.LEVEL_SENSOR_INTERNAL ||
    type === SensorType.PLANT_MODULE ||
    type === SensorType.GSM_PLANT
  );
}

export function supportsMonitoring(type: string): boolean {
  return (
    supportsAnomalies(type) ||
    type === SensorType.GPS_SIMTRACKING ||
    type === SensorType.GPS_GY_NEO ||
    type === 'GPS_GY_NEO'
  );
}

// Family-level setup facts only: actual device settings are not implied by its type.
export function typeSetup(type: string): string {
  switch (type) {
    case SensorType.MACHINE_ACTIVITY: return 'Rapporterede aktivitetsperioder og driftstid; dato efter modtagelse. Aktuel kørsel er ukendt.';
    case SensorType.LEVEL_SENSOR_INTERNAL:
      return 'FLOW-målinger af niveau; beholder, alarmgrænse og ansvarlige vælges pr. sensor.';
    case SensorType.PLANT_MODULE:
      return 'FLOW-anlægsdata; målefelter, dosering og alarmgrænser varierer mellem anlæg.';
    case SensorType.GSM_PLANT:
      return 'Anlægsrapporter via e-mail; kan også have SMS-alarmer og tilpassede datafelter.';
    case SensorType.GSM_CUSTOM:
      return 'SMS-beskeder fra anlæg; ingen løbende kontrol af manglende data.';
    case SensorType.GPS_SIMTRACKING:
      return 'GPSGate/SimTracking; daglig import og opslag af positioner og ture.';
    case SensorType.GPS_GY_NEO:
    case 'GPS_GY_NEO':
      return 'Ældre GPS-integration med direkte indsendelse af positioner.';
    case SensorType.ESP_AQ:
      return 'Luftmålinger via HTTP eller Pub/Sub; alarmer har særskilt enhedskonfiguration.';
    case SensorType.ESP_AQ_NEW:
      return 'Nyere luftkvalitetstype med samme visning; særskilt dataopsætning er ikke dokumenteret.';
    case SensorType.FOOBOT:
      return 'Luftmålinger hentes dagligt fra Foobots API med enhedens ID og API-token.';
    case SensorType.DUST_OLD:
      return 'Ældre støvsensortype uden en særskilt aktuel detaljevisning.';
    default:
      return 'Opsætningen for denne enhedstype er ikke dokumenteret.';
  }
}

function deviceKey({ orgId, sensorId }: { orgId: string; sensorId: string }): string {
  return `${encodeURIComponent(orgId)}:${encodeURIComponent(sensorId)}`;
}

/** Counts only the supplied page(s), not all anomalies ever recorded for a device. */
export function countUnreviewedAnomalies(events: readonly AnomalyEvent[]): number {
  return events.filter(
    (event) => event.review.status === 'pending' || event.review.status === 'investigating'
  ).length;
}

export function fleetIncidentCount(device: FleetDevice, unified = false): number | undefined {
  return unified && device.heartbeat?.alertSetup?.status === 'active'
    ? device.heartbeat.openIncidentCount
    : device.monitoring?.activeIncidentCount;
}

function attentionOrder(device: FleetDevice): number {
  if (device.heartbeat) {
    if (
      (device.heartbeat.heartbeat.ruleMode !== 'test' &&
        device.heartbeat.heartbeat.status === 'overdue') ||
      (fleetIncidentCount(device, true) || 0) > 0
    )
      return 0;
    return countUnreviewedAnomalies(device.anomalies) > 0 ? 2 : 3;
  }
  if (device.monitoring?.currentHealth === 'critical') return 0;
  if (device.monitoring?.currentHealth === 'warning') return 1;
  if (countUnreviewedAnomalies(device.anomalies) > 0) return 2;
  return 3;
}

export function buildFleetDevices(
  sensors: readonly AdminSensorOverviewItem[],
  monitoring?: MonitoringOverview,
  anomalies: readonly AnomalyEvent[] = [],
  heartbeats: readonly UnifiedDevice[] = []
): FleetDevice[] {
  const heartbeatByDevice = new Map(heartbeats.map((device) => [deviceKey(device), device]));
  const monitoringByDevice = new Map(
    (monitoring?.devices || []).map((device) => [deviceKey(device), device])
  );
  const anomaliesByDevice = new Map<string, AnomalyEvent[]>();
  anomalies.forEach((event) => {
    const key = deviceKey(event);
    const events = anomaliesByDevice.get(key) || [];
    events.push(event);
    anomaliesByDevice.set(key, events);
  });

  return sensors
    .map((sensor): FleetDevice => {
      const key = deviceKey(sensor);
      return {
        ...sensor,
        key,
        detailPath: sensorDetailPath(sensor.orgId, sensor.sensorId, sensor.type),
        monitoring: monitoringByDevice.get(key) || null,
        anomalies: anomaliesByDevice.get(key) || [],
        heartbeat: heartbeatByDevice.get(key) || null,
      };
    })
    .sort(
      (left, right) =>
        attentionOrder(left) - attentionOrder(right) ||
        left.orgName.localeCompare(right.orgName, 'da-DK') ||
        left.sensorName.localeCompare(right.sensorName, 'da-DK') ||
        left.key.localeCompare(right.key, 'da-DK')
    );
}

export function filterFleetDevices(
  devices: readonly FleetDevice[],
  filters: Partial<FleetFilters> = {}
): FleetDevice[] {
  const { type = 'all', orgId = 'all', status = 'all', attention = 'all' } = filters;
  const search = (filters.search || '').trim().toLocaleLowerCase('da-DK');
  return devices.filter((device) => {
    if (type !== 'all' && (device.type || 'unknown') !== type) return false;
    if (orgId !== 'all' && device.orgId !== orgId) return false;
    if (status !== 'all' && device.status !== status) return false;
    if (!matchesOperatingMode(device.heartbeat?.mode.mode, filters.mode || 'all')) return false;
    if (
      attention === 'alarms' &&
      !(
        fleetIncidentCount(device, filters.unified) || 0
      )
    )
      return false;
    if (attention === 'anomalies' && countUnreviewedAnomalies(device.anomalies) === 0) return false;
    if (
      attention === 'unmonitored' &&
      (filters.unified
        ? device.heartbeat?.heartbeat.status !== 'unsupported'
        : supportsMonitoring(device.type))
    )
      return false;
    return (
      !search ||
      [
        device.orgName,
        device.orgId,
        device.sensorName,
        device.sensorId,
        device.deviceId || '',
        device.type,
        sensorTypeLabel(device.type),
      ]
        .join(' ')
        .toLocaleLowerCase('da-DK')
        .includes(search)
    );
  });
}

export function getAnomalyPath(device: { orgId: string; sensorId: string }): string {
  const query = new URLSearchParams({ orgId: device.orgId, sensorId: device.sensorId });
  return `/monitoring/anomalies?${query.toString()}`;
}
